import { SdkContext } from '../internal'

/**
 * F-85: custom member fields, spreadsheet imports and the invite opt-out.
 *
 * Paths follow PROPOSTA-IMPORTACAO-MEMBROS.md §5. Every route reads the
 * community from `X-CommunityId`; none carries it in the path.
 */

export type MemberFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'MULTI_SELECT' | 'BOOLEAN' | 'EMAIL' | 'PHONE' | 'URL'

export const MEMBER_FIELD_TYPES: MemberFieldType[] = ['TEXT', 'NUMBER', 'DATE', 'SELECT', 'MULTI_SELECT', 'BOOLEAN', 'EMAIL', 'PHONE', 'URL']

/** The types whose answer is one of the field's options (F-149: MULTI_SELECT takes several). */
export const CHOICE_FIELD_TYPES: MemberFieldType[] = ['SELECT', 'MULTI_SELECT']

/** F-149: a MULTI_SELECT has 2 to 50 options, each label at most 80 characters (a SELECT up to 120). */
export const MULTI_OPTIONS_MAX = 50
export const MULTI_OPTION_LABEL_MAX = 80

export interface MemberFieldOption {
  value: string
  label: string
}

/** A custom field of the community (`/member-fields`). */
export interface MemberField {
  id: string
  /** Stable slug, used in `{{campo_<key>}}`. Never changes after creation. */
  key: string
  label: string
  type: MemberFieldType
  options?: MemberFieldOption[] | null
  /** MULTI_SELECT only: the fewest and most options a member may mark (null = no bound). */
  minSelections?: number | null
  maxSelections?: number | null
  required: boolean
  /** Asked on the community signup form; always true while `required`. */
  askOnSignup?: boolean
  /** TEXT only: the whole value must match (RE2 syntax, anchored on the server). */
  pattern?: string | null
  /** TEXT only: the hint shown when the value does not match `pattern`. */
  patternMessage?: string | null
  order: number
  /** True once some member holds a value: the type can no longer change. */
  hasValues?: boolean
  /** F-177: on the member page, as saved or as the automatic rule resolved it. */
  profileVisibility?: 'NEVER' | 'MEMBER_CHOOSES' | 'ALWAYS'
  /** True while nobody chose: the server decides by type and label. */
  profileVisibilityAutomatic?: boolean
  /** Per-item permissions, when the server sends them. */
  canEdit?: boolean
  canDelete?: boolean
}

/** A field value as it comes on a member (`fields` in `GET /members/:id` and `/auth/me`). */
export interface MemberFieldValue {
  id?: string
  key: string
  label: string
  type: MemberFieldType
  /** A MULTI_SELECT carries the array of marked option values. */
  value: string | string[] | null
  options?: MemberFieldOption[] | null
  minSelections?: number | null
  maxSelections?: number | null
  required?: boolean
  pattern?: string | null
  patternMessage?: string | null
}

export interface MemberFieldInput {
  label: string
  type: MemberFieldType
  options?: MemberFieldOption[]
  minSelections?: number | null
  maxSelections?: number | null
  required?: boolean
  askOnSignup?: boolean
  pattern?: string | null
  patternMessage?: string | null
  /** F-177: on the member page; `null` = automatic (sensitive fields NEVER, the rest MEMBER_CHOOSES). */
  profileVisibility?: 'NEVER' | 'MEMBER_CHOOSES' | 'ALWAYS' | null
}

/** A field the community signup form asks (`GET /member-fields/signup`, public). */
export interface SignupField {
  key: string
  label: string
  type: MemberFieldType
  options?: MemberFieldOption[] | null
  minSelections?: number | null
  maxSelections?: number | null
  required: boolean
  pattern?: string | null
  patternMessage?: string | null
  order: number
}

/* ------------------------------------------------------------- imports -- */

export type ImportTarget =
  | 'ignore'
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'jobTitle'
  | 'tagline'
  | 'tags'
  | 'groups'
  /** F-167: one date per row, the "Válido até" of the groups the row gives (invalid or past: a warning, the groups stay permanent). */
  | 'groupsExpireAt'
  | 'consent'
  /** F-142: the SMS consent column; a "yes" counts only with the attestation. */
  | 'smsConsent'
  | 'field'
  | 'newField'
  /** F-157: badge names of this community's catalog, `;`-separated (one column). */
  | 'badges'
  /** F-157: an integer from 1 to 100,000, given once per person (one column; gamification on). */
  | 'points'

export interface ImportColumn {
  index: number
  /** The header as shown, with "(col. N)" when the name repeats. */
  name: string
  /** The header exactly as it came in the file. */
  header?: string
  /** The values of the first three data rows. */
  samples: string[]
  suggestion?: {
    target: ImportTarget
    fieldKey?: string | null
    confidence: 'high' | 'low'
  } | null
  /** The type a new custom field would get (§6.3). */
  inferredType?: MemberFieldType
  /** Distinct values seen in the column, for the consent "yes" picker. */
  distinctValues?: string[]
}

export interface ImportAnalysis {
  id: string
  fileName: string
  status?: ImportStatus
  fileType?: 'csv' | 'xlsx'
  sheets?: { name: string; rows: number }[]
  sheet?: string | null
  encoding?: string | null
  delimiter?: string | null
  headerRow: number
  /** False when no row looked like a header and row 1 was taken (§6.1). */
  headerDetected?: boolean
  /** The first raw rows of the file, to pick another header row. */
  rawRows?: string[][]
  columns: ImportColumn[]
  totalRows: number
  summary?: ImportSummary | null
}

export interface ImportMappingEntry {
  column: number
  target: ImportTarget
  /** For `field`: the existing field. */
  fieldKey?: string
  /** For `newField`: the field to create. */
  newField?: { label: string; type: MemberFieldType }
  /** F-157, for `badges`: names not in the catalog that are left out instead of blocking the start. */
  ignoreNames?: string[]
}

export interface ImportOptions {
  role: 'member' | 'moderator'
  groupIds: string[]
  tags: string[]
  origin: string
  /** Values of the consent column that count as "yes". */
  consentValues?: string[]
  /** Accepted declaration, when no consent column was chosen. */
  consentDeclaration?: boolean
  /** F-142: values of the SMS consent column that count as "yes". */
  smsConsentValues?: string[]
  /** F-142: the community attests it collected that consent (the date is the import's). */
  smsConsentAttestation?: boolean
}

export interface ImportSummary {
  totalRows: number
  newPeople: number
  alreadyMembers: number
  skipped: number
  skippedByReason?: Record<string, number>
  declined: number
  emailsToSend: number
  newGroups: string[]
  newTags: string[]
  newFields: number
  /** F-149: per mapped SELECT or MULTI_SELECT field, the sheet's values that are not options yet (up to 50). */
  unknownOptions?: ImportUnknownOptions[]
  /** F-157: with a `badges` column. `unknown` must be created or ignored before the start (else 400 `badgesUnknown`). */
  badges?: ImportBadgesSummary
  /** F-157: with a `points` column. */
  points?: ImportPointsSummary
}

export interface ImportBadgesSummary {
  /** Names not in this community's catalog. */
  unknown: string[]
  /** The names the mapping entry ignores. */
  ignored: string[]
  /** How many badges will be granted. */
  grants: number
}

export interface ImportPointsSummary {
  /** Rows that will get points. */
  rows: number
  invalidCount: number
  /** Up to 50 rows whose value is not an integer from 1 to 100,000. */
  invalid: { rowNumber: number; value: string }[]
  /** People who already got points from an earlier import: not again. */
  alreadyImported: number
  /** Gamification off: the points are ignored. */
  gamificationOff: boolean
  /** The level each person with points lands on (no weekly activity yet). */
  levels: { level: number; name: string; count: number }[]
}

export interface ImportUnknownOptions {
  fieldKey: string
  fieldId: string
  label: string
  type: MemberFieldType
  values: string[]
}

export type ImportStatus = 'ANALYZED' | 'QUEUED' | 'PROCESSING' | 'DONE' | 'FAILED' | 'ABANDONED'
  /** F-86: stopped at the next batch because the SuperAdmin suspended the community. */
  | 'INTERRUPTED'

/** A value that did not fit and was left empty; the row still came in. */
export interface ImportRowWarning {
  /** The column header, when the warning belongs to one. */
  column?: string | null
  /** The custom field key, for `INVALID_VALUE`. */
  field?: string
  reason: 'INVALID_VALUE' | 'INVALID_PHONE' | 'INVALID_TAG' | string
  value?: string
}

export interface ImportRowResult {
  rowNumber: number
  email: string | null
  result: 'CREATED' | 'UPDATED' | 'SKIPPED'
  reason?: string | null
  warnings?: ImportRowWarning[] | null
}

/** The signed link to the original file (5 minutes). */
export interface ImportFileLink {
  url: string
  expiresAt?: string
  fileName?: string
}

export interface MemberImport {
  id: string
  status: ImportStatus
  fileName: string
  origin?: string
  role?: 'member' | 'moderator'
  groupIds?: string[]
  tags?: string[]
  legalBasis?: string | null
  /** False when no backup of the original file was kept. */
  hasFile?: boolean
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
  createdBy?: { id: string; name: string | null } | null
  totalRows: number
  processedRows?: number
  createdCount: number
  updatedCount: number
  skippedCount: number
  invitedCount: number
  /** The skipped rows with their reason and the rows that came in with warnings (the report). */
  rows?: ImportRowResult[]
  consentColumn?: string | null
  consentValues?: string[]
  consentDeclaration?: string | null
}

/** One entry of the invite answer, per e-mail. */
export type InviteOutcome = 'invited' | 'updated' | 'declined' | 'invalid' | 'skipped' | 'failed'

export interface InviteResult {
  email: string
  status: InviteOutcome
  /** The server's own words for it, in the person's language. */
  message?: string
}

const listOf = <T>(result: unknown): T[] => {
  if (Array.isArray(result)) return result as T[]
  const r = result as { items?: T[]; data?: T[] } | null
  return r?.items ?? r?.data ?? []
}

export class MemberFieldsResource {
  constructor(private ctx: SdkContext) {}

  async list(): Promise<MemberField[]> {
    return listOf<MemberField>(await this.ctx.raw({ httpMethod: 'GET', path: '/member-fields' }))
  }

  /** The fields the signup form asks: required ones plus those marked `askOnSignup`. Public. */
  async signup(): Promise<SignupField[]> {
    return listOf<SignupField>(await this.ctx.raw({ httpMethod: 'GET', path: '/member-fields/signup' }))
  }

  async create(body: MemberFieldInput): Promise<MemberField> {
    return this.ctx.call({ httpMethod: 'POST', path: '/member-fields', hasBody: true }, { body })
  }

  async update(id: string, body: Partial<MemberFieldInput>): Promise<MemberField> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/member-fields/{id}', hasBody: true }, { id, body })
  }

  async remove(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/member-fields/{id}' }, { id })
  }

  /** The new order, every id of the community in sequence. */
  async reorder(ids: string[]): Promise<void> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/member-fields/order', hasBody: true }, { body: { ids } })
  }

  /**
   * A member's values, keyed by field key. `null` or `''` clears one. Owner,
   * admin, moderator or the member themself; required fields are validated.
   */
  async setMemberValues(memberId: string, values: Record<string, string | string[] | null>): Promise<{ fields?: MemberFieldValue[] }> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/members/{memberId}/fields', hasBody: true },
      { memberId, body: { values } }
    )
  }
}

export class MemberImportsResource {
  constructor(private ctx: SdkContext) {}

  /** Uploads the file and answers the analysis (§2.2 step 1). */
  async analyze(file: File): Promise<ImportAnalysis> {
    const fd = new FormData()
    fd.append('file', file)
    return this.ctx.call({ httpMethod: 'POST', path: '/members/imports', hasBody: true }, { body: fd })
  }

  /**
   * Re-analyzes with another sheet, header row, delimiter or encoding; with a
   * mapping and options, also answers the summary of step 4. Writes nothing.
   */
  async preview(
    id: string,
    body: {
      sheet?: string
      headerRow?: number
      delimiter?: string
      encoding?: string
      mapping?: ImportMappingEntry[]
      options?: ImportOptions
    }
  ): Promise<ImportAnalysis> {
    return this.ctx.call({ httpMethod: 'POST', path: '/members/imports/{id}/preview', hasBody: true }, { id, body })
  }

  /** Confirms and queues (202). */
  async start(
    id: string,
    body: { mapping: ImportMappingEntry[]; options: ImportOptions; sheet?: string; headerRow?: number; delimiter?: string; encoding?: string }
  ): Promise<MemberImport> {
    return this.ctx.call({ httpMethod: 'POST', path: '/members/imports/{id}/start', hasBody: true }, { id, body })
  }

  async list(): Promise<MemberImport[]> {
    return listOf<MemberImport>(await this.ctx.raw({ httpMethod: 'GET', path: '/members/imports' }))
  }

  async get(id: string): Promise<MemberImport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/imports/{id}' }, { id })
  }

  /**
   * The skipped rows as CSV text, with the "reason" column. The transport reads
   * `text/csv` as text, and `fetch` drops the BOM on the way: it goes back so
   * spreadsheet apps keep reading the file as UTF-8.
   */
  async errorsCsv(id: string): Promise<string> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/members/imports/{id}/errors.csv' }, { id })
    const text = typeof result === 'string' ? result : String(result ?? '')
    return text.startsWith('\uFEFF') ? text : '\uFEFF' + text
  }

  /** The backup of the original file: a signed link that lasts 5 minutes. */
  async file(id: string): Promise<ImportFileLink> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/imports/{id}/file' }, { id })
  }
}

export class InviteOptOutResource {
  constructor(private ctx: SdkContext) {}

  /** Public: the community the link belongs to, and whether it was already used. */
  async get(token: string): Promise<{ communityName: string; email?: string; alreadyDeclined?: boolean; expired?: boolean }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/invite-opt-out/{token}' }, { token })
  }

  /** Public: removes the member and records the opt-out. */
  async confirm(token: string): Promise<{ communityName?: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/invite-opt-out/{token}', hasBody: true }, { token })
  }

  /** The same, from inside the community (the imported person, logged in). */
  async declineFromCommunity(): Promise<void> {
    return this.ctx.call({ httpMethod: 'POST', path: '/members/me/decline', hasBody: true })
  }
}

/** True when the route is not in this API yet (404 on the path itself). */
export const isMissingRoute = (error: unknown): boolean => {
  const e = error as { status?: number; response?: { status?: number } } | null
  return (e?.status ?? e?.response?.status) === 404
}

interface ApiErrorShape {
  message?: string
  response?: { status?: number; data?: { message?: string; errors?: { param?: string; message?: string; reason?: string }[] } }
}

/** The first message an API error carries: a field error, the top message, or the fallback. */
export const apiErrorText = (error: unknown, fallback: string): string => {
  const e = (error ?? {}) as ApiErrorShape
  return e.response?.data?.errors?.[0]?.message || e.response?.data?.message || e.message || fallback
}

/** Field errors out of an API 400 (`errors[].param` is the field key, or `values.<key>`). */
export const apiFieldErrors = (error: unknown): Record<string, string> => {
  const list = ((error ?? {}) as ApiErrorShape).response?.data?.errors ?? []
  return Object.fromEntries(
    list.filter((e) => e?.param).map((e) => [String(e.param).replace(/^values\.|^fields\./, ''), e.message ?? ''])
  )
}
